"use client";

import React from "react";
import Image from "next/image";
import * as Dialog from "@radix-ui/react-dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { X } from "lucide-react";
import { ButtonSize, ButtonVariant, RegularButton } from "@/stories/button/button";
import Apple from "@/app/assets/icons/apple";
import GooglePlay from "@/app/assets/icons/googleplay";
import { DialogTitle } from "@/components/ui/dialog";
// import { handleAppDownload } from "@/app/utils/helper";
import { IMAGE_TYPE } from "@/app/enums/ui";

interface AppModalProps {
  open: boolean;
  onClose?: () => void;
}

const GetICMAppModal: React.FC<AppModalProps> = ({ open, onClose }) => {
  return (
    <Dialog.Root open={open} onOpenChange={onClose} data-testid="app-download-dialog">
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-gray-300/98 backdrop-blur-lg" data-testid="app-download-overlay" />
        <Dialog.Content 
          className="fixed bottom-0 left-0 right-0 w-full max-h-max bg-white shadow-lg transition-all duration-300 ease-in-out rounded-t-[24px]
            md:bottom-auto md:h-[436px] md:min-w-[568px] md:rounded-[24px] md:top-1/2 md:left-1/2 md:w-[90%] md:max-w-md md:-translate-x-1/2 md:-translate-y-1/2"
          data-testid="app-download-content"
        >
          <VisuallyHidden.Root>
              <DialogTitle data-testid="app-download-title"></DialogTitle>
          </VisuallyHidden.Root>
          <Dialog.Close asChild>
            <button
              className="absolute md:top-2 top-8 md:right-1 right-0 p-2 text-neutral-900"
              onClick={onClose}
              data-testid="app-download-close-btn"
            >
              <X size={28} />
            </button>
          </Dialog.Close>
          <div className="flex flex-col gap-8 mt-0 md:my-14" data-testid="app-download-body">
            <p className="text-neutral-900 text-center text-lg md:text-4xl font-Semibold leading-[130%] tracking-[-0.48px]" data-testid="app-download-heading">
              Get the InCred Money app
            </p>
            
            <div className="flex justify-center items-center w-full gap-6 h-[144px] md:h-[222px]" data-testid="app-download-options">
                <div className="hidden md:block flex flex-col h-full justify-between min-w-full md:min-w-[200px] md:w-[200px]" data-testid="qr-code-section">
                    <p className="text-neutral-300 text-center text-lg font-normal leading-[164%] tracking-[-0.18px]" data-testid="qr-code-text">
                      Scan this QR code
                    </p>
                    <Image
                        src={process.env.ICM_IMAGES_URL + IMAGE_TYPE.SVG + '/qr-code.svg'}
                        alt=""
                        height={158}
                        width={158}
                        className="rounded-md m-auto mt-8"
                        data-testid="qr-code-image"
                    />
                </div>
                <div className="hidden md:block h-[144px] md:h-[222px] border-l-2 border-dashed border-gray-500" data-testid="divider"></div>
                <div className="flex flex-col h-full justify-between min-w-full md:min-w-[240px] md:w-[240px]" data-testid="download-buttons-section">
                    <p className="hidden md:block text-neutral-300 text-center text-lg font-normal leading-[164%] tracking-[-0.18px] mb-6" data-testid="download-app-text">
                      Or, download the mobile app
                    </p>
                    <div className="flex flex-col gap-4 items-center min-w-full" data-testid="app-store-buttons">
                        <RegularButton
                            showRightIcon={false}
                            icon={<Apple />}
                            size={ButtonSize.Large}
                            variant={ButtonVariant.SecondaryDefault}
                            className="w-full min-w-full bg-white"
                            label={'iOS'}
                            onClick={() => window.open(process.env.ICM_APPLE_APP_URL)}
                            data-testid="ios-download-btn"
                        />
                        <RegularButton
                            showRightIcon={false}
                            icon={<GooglePlay />}
                            size={ButtonSize.Large}
                            variant={ButtonVariant.SecondaryDefault}
                            className="w-full min-w-full bg-white"
                            label={'Android'}
                            onClick={() => window.open(process.env.ICM_PLAYSTORE_URL)}
                            data-testid="android-download-btn"
                        />
                    </div>
                </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default GetICMAppModal;